import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, Plus, Trash2, ExternalLink, Mail, Phone, Star, Award,
  TrendingUp, AlertTriangle, Users as UsersIcon, Pencil, X as XIcon, Check, Scale,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PageHeader } from "@/components/common/PageHeader";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Phase 12 — Agent Evaluation (Layer 3 · Strategy)
// ---------------------------------------------------------------------------
// Scope:
//   - admin can CRUD; family role is read-only on agents that are
//     visible_to_family (default true) — backend enforces.
//   - Single page covers list, create, edit, score, compare-up-to-3.
// ---------------------------------------------------------------------------

const STATUS_TONE = {
  under_review: { tone: "neutral", label: "Under review" },
  preferred:    { tone: "cyan",    label: "Preferred"   },
  signed:       { tone: "emerald", label: "Signed"      },
  declined:     { tone: "rose",    label: "Declined"    },
};

function emptyAgent() {
  return {
    name: "",
    agency: "",
    role: "Lead Agent",
    years_experience: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    fee_pct: 3,
    notable_clients: [],
    current_clients_count: "",
    strengths: [],
    concerns: [],
    notes_md: "",
    scores: {},
    status: "under_review",
    visible_to_family: true,
  };
}

export default function AgentsPage() {
  useDocumentTitle("OmniDraftLab · Agent Evaluation");
  const { user } = useAuth();
  const { playerId: paramPlayerId } = useParams();
  const isAdmin = user?.role === "admin";

  // For non-admin roles the page resolves to their linked athlete.
  const playerId = paramPlayerId || user?.player_id;

  const [dimensions, setDimensions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // agent or "new"
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!playerId) {
      setAgents([]);
      return;
    }
    const r = await api.get("/agents", { params: { player_id: playerId } });
    setAgents(r.data || []);
  }, [playerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/agents/config/dimensions").then((r) => { if (!cancelled) setDimensions(r.data.dimensions || []); }),
      refresh(),
    ]).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast.info("Compare up to 3 agents at a time.");
        return prev;
      }
      return [...prev, id];
    });
  };

  const compareList = useMemo(
    () => agents.filter((a) => compareIds.includes(a.id)),
    [agents, compareIds]
  );

  if (loading) return <LoadingState />;

  if (!playerId) {
    return (
      <EmptyState
        icon={Briefcase}
        title="No athlete linked"
        description="Your account isn't linked to an athlete yet."
      />
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="agents-page">
      <PageHeader
        title="Agent Evaluation"
        subtitle="Vet every agent on the same 8-dimension scorecard. The right rep changes the entire trajectory."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {compareIds.length >= 2 && (
              <Button
                data-testid="agents-compare-open"
                variant="ghost"
                onClick={() => setCompareOpen(true)}
                className="text-2 hover:text-white"
              >
                <Scale className="h-4 w-4 mr-1" /> Compare ({compareIds.length})
              </Button>
            )}
            {isAdmin && (
              <Button
                data-testid="agents-new"
                onClick={() => setEditing("new")}
                className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-xl"
              >
                <Plus className="h-4 w-4 mr-1" /> New agent
              </Button>
            )}
          </div>
        }
      />

      {agents.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={Briefcase}
            title="No agents on the board yet"
            description={isAdmin ? "Add the first agent to start the evaluation." : "Your advisor hasn't shared agent evaluations with you yet."}
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-12 gap-4 md:gap-5">
          {agents.map((a, i) => (
            <AgentCard
              key={a.id}
              agent={a}
              index={i}
              dimensions={dimensions}
              isAdmin={isAdmin}
              compareSelected={compareIds.includes(a.id)}
              onToggleCompare={() => toggleCompare(a.id)}
              onEdit={() => setEditing(a)}
              onDelete={async () => {
                if (!confirm(`Remove ${a.name} from the board?`)) return;
                await api.delete(`/agents/${a.id}`);
                toast.success("Agent removed");
                setCompareIds((p) => p.filter((x) => x !== a.id));
                await refresh();
              }}
            />
          ))}
        </div>
      )}

      {editing && (
        <AgentDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          initial={editing === "new" ? emptyAgent() : editing}
          isNew={editing === "new"}
          dimensions={dimensions}
          playerId={playerId}
          onSaved={async (msg) => { toast.success(msg); setEditing(null); await refresh(); }}
        />
      )}

      <CompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        agents={compareList}
        dimensions={dimensions}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent card
// ---------------------------------------------------------------------------
function AgentCard({ agent, index, dimensions, isAdmin, compareSelected, onToggleCompare, onEdit, onDelete }) {
  const status = STATUS_TONE[agent.status] || STATUS_TONE.under_review;
  const composite = agent.composite_score;
  const tone = composite == null
    ? "neutral"
    : composite >= 8
      ? "emerald"
      : composite >= 6
        ? "cyan"
        : composite >= 4
          ? "amber"
          : "rose";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      className="col-span-12 md:col-span-6 lg:col-span-4"
      data-testid={`agent-card-${agent.id}`}
    >
      <GlassCard className="p-6 h-full flex flex-col" hover>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-3 text-[10px] uppercase tracking-wider">
              <Briefcase className="h-3 w-3" /> {agent.role || "Agent"}
            </div>
            <h3 className="mt-1 text-1 text-xl font-semibold tracking-tight truncate">{agent.name}</h3>
            {agent.agency && <div className="text-2 text-sm truncate">{agent.agency}</div>}
          </div>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </div>

        {/* Composite anchor */}
        <div className="mt-5 flex items-end justify-between">
          <div>
            <div className="text-3 text-[10px] uppercase tracking-wider">Composite</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-1 font-semibold text-4xl font-mono leading-none">
                {composite != null ? composite.toFixed(1) : "—"}
              </span>
              <span className="text-3 text-sm">/ 10</span>
            </div>
          </div>
          <div className="text-right">
            {agent.fee_pct != null && (
              <div className="text-3 text-[10px] uppercase tracking-wider">Fee · <span className="text-1 font-mono text-xs">{agent.fee_pct}%</span></div>
            )}
            {agent.years_experience != null && (
              <div className="text-3 text-[10px] uppercase tracking-wider mt-0.5">Exp · <span className="text-1 font-mono text-xs">{agent.years_experience}y</span></div>
            )}
            {agent.current_clients_count != null && (
              <div className="text-3 text-[10px] uppercase tracking-wider mt-0.5">Clients · <span className="text-1 font-mono text-xs">{agent.current_clients_count}</span></div>
            )}
          </div>
        </div>

        {/* Composite bar */}
        <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              tone === "emerald" && "bg-emerald-400",
              tone === "cyan" && "bg-cyan-300",
              tone === "amber" && "bg-amber-400",
              tone === "rose" && "bg-rose-400",
              tone === "neutral" && "bg-white/30",
            )}
            style={{ width: `${((composite || 0) / 10) * 100}%` }}
          />
        </div>

        {/* Mini score grid */}
        <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5">
          {dimensions.map((d) => {
            const v = agent.scores?.[d.key];
            return (
              <div key={d.key} className="flex items-center gap-2 text-xs">
                <span className="text-3 truncate flex-1">{d.label}</span>
                <span className={cn(
                  "font-mono w-7 text-right",
                  v == null ? "text-3" : v >= 8 ? "text-emerald-300" : v >= 6 ? "text-cyan-300" : v >= 4 ? "text-amber-300" : "text-rose-300"
                )}>
                  {v != null ? v.toFixed(1) : "—"}
                </span>
              </div>
            );
          })}
        </div>

        {agent.strengths?.length > 0 && (
          <div className="mt-4">
            <div className="text-emerald-300 text-[10px] uppercase tracking-wider flex items-center gap-1.5"><TrendingUp className="h-3 w-3" /> Strengths</div>
            <ul className="mt-1.5 space-y-0.5">
              {agent.strengths.slice(0, 3).map((s, i) => (
                <li key={i} className="text-2 text-xs leading-snug flex gap-1.5"><span className="text-emerald-300">·</span> <span>{s}</span></li>
              ))}
            </ul>
          </div>
        )}
        {agent.concerns?.length > 0 && (
          <div className="mt-3">
            <div className="text-rose-300 text-[10px] uppercase tracking-wider flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" /> Concerns</div>
            <ul className="mt-1.5 space-y-0.5">
              {agent.concerns.slice(0, 2).map((s, i) => (
                <li key={i} className="text-2 text-xs leading-snug flex gap-1.5"><span className="text-rose-300">·</span> <span>{s}</span></li>
              ))}
            </ul>
          </div>
        )}

        {agent.notable_clients?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {agent.notable_clients.slice(0, 4).map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/5 ring-1 ring-white/10 text-2">
                <Star className="h-2.5 w-2.5" /> {c}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 flex-wrap text-3 text-[11px]">
          {agent.contact_email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {agent.contact_email}</span>}
          {agent.contact_phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {agent.contact_phone}</span>}
          {agent.website && (
            <a href={agent.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200">
              <ExternalLink className="h-3 w-3" /> Site
            </a>
          )}
        </div>

        <div className="mt-auto pt-4 flex items-center justify-between gap-2">
          <button
            data-testid={`agent-compare-${agent.id}`}
            onClick={onToggleCompare}
            className={cn(
              "inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ring-1 transition",
              compareSelected
                ? "bg-cyan-300/15 text-cyan-200 ring-cyan-300/40"
                : "bg-white/[0.03] text-2 ring-white/10 hover:ring-cyan-300/30"
            )}
          >
            <Scale className="h-3 w-3" /> {compareSelected ? "Selected" : "Compare"}
          </button>
          {isAdmin && (
            <div className="flex items-center gap-1">
              <Button data-testid={`agent-edit-${agent.id}`} onClick={onEdit} variant="ghost" size="icon" className="text-2 hover:text-white">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button data-testid={`agent-delete-${agent.id}`} onClick={onDelete} variant="ghost" size="icon" className="text-2 hover:text-rose-300">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit dialog
// ---------------------------------------------------------------------------
function AgentDialog({ open, onClose, initial, isNew, dimensions, playerId, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setScore = (k, v) => set({ scores: { ...(form.scores || {}), [k]: v } });

  const composite = useMemo(() => {
    const vals = dimensions.map((d) => form.scores?.[d.key]).filter((v) => v != null && !Number.isNaN(v));
    if (!vals.length) return null;
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
  }, [form.scores, dimensions]);

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Agent name is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        current_clients_count: form.current_clients_count === "" ? null : Number(form.current_clients_count),
        fee_pct: form.fee_pct === "" || form.fee_pct == null ? null : Number(form.fee_pct),
      };
      if (isNew) {
        await api.post("/agents", { ...payload, player_id: playerId });
        await onSaved(`${form.name} added to the board`);
      } else {
        await api.patch(`/agents/${form.id}`, payload);
        await onSaved(`${form.name} updated`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass-strong border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agent-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">{isNew ? "Add agent" : "Edit agent"}</DialogTitle>
          <DialogDescription className="text-3 text-xs">
            Score on the 8-dimension rubric. Composite is auto-computed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4 mt-2">
          <div className="col-span-12 md:col-span-6 space-y-1.5">
            <Label className="text-2 text-xs">Name *</Label>
            <Input data-testid="agent-form-name" value={form.name || ""} onChange={(e) => set({ name: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 md:col-span-6 space-y-1.5">
            <Label className="text-2 text-xs">Agency</Label>
            <Input data-testid="agent-form-agency" value={form.agency || ""} onChange={(e) => set({ agency: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 md:col-span-4 space-y-1.5">
            <Label className="text-2 text-xs">Role</Label>
            <Select value={form.role || "Lead Agent"} onValueChange={(v) => set({ role: v })}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {["Lead Agent", "Marketing Agent", "Financial Advisor", "Lawyer", "Other"].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-2 space-y-1.5">
            <Label className="text-2 text-xs">Exp (yrs)</Label>
            <Input data-testid="agent-form-exp" type="number" value={form.years_experience ?? ""} onChange={(e) => set({ years_experience: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-6 md:col-span-2 space-y-1.5">
            <Label className="text-2 text-xs">Fee %</Label>
            <Input type="number" step="0.1" value={form.fee_pct ?? ""} onChange={(e) => set({ fee_pct: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-6 md:col-span-2 space-y-1.5">
            <Label className="text-2 text-xs">Clients</Label>
            <Input type="number" value={form.current_clients_count ?? ""} onChange={(e) => set({ current_clients_count: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 md:col-span-2 space-y-1.5">
            <Label className="text-2 text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger data-testid="agent-form-status" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(STATUS_TONE).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-12 md:col-span-6 space-y-1.5">
            <Label className="text-2 text-xs">Email</Label>
            <Input value={form.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-6 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Phone</Label>
            <Input value={form.contact_phone || ""} onChange={(e) => set({ contact_phone: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-6 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Website</Label>
            <Input value={form.website || ""} onChange={(e) => set({ website: e.target.value })} className="bg-white/5 border-white/10" />
          </div>

          <ChipListField
            className="col-span-12 md:col-span-6"
            label="Notable clients"
            placeholder="Add a client and press Enter"
            items={form.notable_clients}
            onChange={(items) => set({ notable_clients: items })}
            testId="agent-form-clients"
          />
          <ChipListField
            className="col-span-12 md:col-span-3"
            label="Strengths"
            placeholder="Add a strength"
            items={form.strengths}
            onChange={(items) => set({ strengths: items })}
            testId="agent-form-strengths"
          />
          <ChipListField
            className="col-span-12 md:col-span-3"
            label="Concerns"
            placeholder="Add a concern"
            items={form.concerns}
            onChange={(items) => set({ concerns: items })}
            testId="agent-form-concerns"
          />

          {/* Scorecard */}
          <div className="col-span-12 mt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-1 text-sm font-semibold">8-Dimension Scorecard</Label>
              <div className="flex items-center gap-2">
                <span className="text-3 text-[10px] uppercase tracking-wider">Composite</span>
                <span className="text-1 font-mono text-lg">{composite != null ? composite.toFixed(1) : "—"}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {dimensions.map((d) => (
                <ScoreSliderRow
                  key={d.key}
                  label={d.label}
                  description={d.description}
                  value={form.scores?.[d.key]}
                  onChange={(v) => setScore(d.key, v)}
                  testId={`agent-score-${d.key}`}
                />
              ))}
            </div>
          </div>

          <div className="col-span-12 space-y-1.5">
            <Label className="text-2 text-xs">Notes (markdown)</Label>
            <Textarea
              rows={4}
              value={form.notes_md || ""}
              onChange={(e) => set({ notes_md: e.target.value })}
              className="bg-white/5 border-white/10 text-1"
              placeholder="Free-form notes — meeting summaries, due diligence, references…"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button data-testid="agent-save" onClick={save} disabled={busy} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold">
            <Check className="h-4 w-4 mr-1" /> {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Score slider row (1-10, optional)
// ---------------------------------------------------------------------------
function ScoreSliderRow({ label, description, value, onChange, testId }) {
  const v = value == null ? null : Number(value);
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="text-1 text-sm font-medium leading-tight truncate">{label}</div>
        {description && <div className="text-3 text-[11px] truncate">{description}</div>}
      </div>
      <div className="w-32 shrink-0">
        <Slider
          min={1}
          max={10}
          step={0.5}
          value={[v ?? 1]}
          onValueChange={(arr) => onChange(arr[0])}
        />
      </div>
      <div className={cn(
        "w-9 text-right font-mono text-sm shrink-0",
        v == null ? "text-3" : v >= 8 ? "text-emerald-300" : v >= 6 ? "text-cyan-300" : v >= 4 ? "text-amber-300" : "text-rose-300"
      )}>
        {v != null ? v.toFixed(1) : "—"}
      </div>
      {v != null && (
        <button
          onClick={() => onChange(null)}
          className="text-3 hover:text-rose-300 shrink-0"
          title="Clear score"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip-list input
// ---------------------------------------------------------------------------
function ChipListField({ className, label, placeholder, items, onChange, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label className="text-2 text-xs">{label}</Label>
      <div className="flex gap-2">
        <Input
          data-testid={testId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="bg-white/5 border-white/10"
        />
        <Button type="button" variant="ghost" onClick={add} className="text-2 hover:text-white shrink-0">Add</Button>
      </div>
      {(items || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {items.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white/5 ring-1 ring-white/10 text-1">
              {it}
              <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-3 hover:text-rose-300">
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compare dialog (up to 3)
// ---------------------------------------------------------------------------
function CompareDialog({ open, onClose, agents, dimensions }) {
  if (!agents.length) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass-strong border-white/10 max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="agent-compare-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">Side-by-side · {agents.length} agents</DialogTitle>
          <DialogDescription className="text-3 text-xs">Same rubric, three lenses. The greenest column wins per row.</DialogDescription>
        </DialogHeader>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-3 text-[11px] uppercase tracking-wider">
                <th className="text-left py-2 pr-3 sticky left-0 bg-[color:var(--canvas-2)] z-10">Dimension</th>
                {agents.map((a) => (
                  <th key={a.id} className="text-left py-2 px-3 min-w-[200px]">
                    <div className="text-1 text-sm font-semibold">{a.name}</div>
                    <div className="text-3 text-[11px]">{a.agency || "—"}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-white/5 bg-white/[0.02]">
                <td className="py-3 pr-3 text-1 font-semibold sticky left-0 bg-[color:var(--canvas-2)]">Composite</td>
                {agents.map((a) => {
                  const max = Math.max(...agents.map((x) => x.composite_score || 0));
                  const isBest = (a.composite_score || 0) === max && max > 0;
                  return (
                    <td key={a.id} className={cn("py-3 px-3 font-mono text-base", isBest && "text-emerald-300 font-semibold")}>
                      {a.composite_score != null ? a.composite_score.toFixed(1) : "—"}{isBest && " ★"}
                    </td>
                  );
                })}
              </tr>
              {dimensions.map((d) => {
                const vals = agents.map((a) => a.scores?.[d.key] ?? null);
                const max = Math.max(...vals.filter((v) => v != null), 0);
                return (
                  <tr key={d.key} className="border-t border-white/5">
                    <td className="py-2.5 pr-3 sticky left-0 bg-[color:var(--canvas-2)]">
                      <div className="text-1">{d.label}</div>
                      <div className="text-3 text-[10px]">{d.description}</div>
                    </td>
                    {agents.map((a, i) => {
                      const v = vals[i];
                      const isBest = v != null && v === max && max > 0;
                      return (
                        <td key={a.id} className={cn(
                          "py-2.5 px-3 font-mono",
                          v == null ? "text-3" : v >= 8 ? "text-emerald-300" : v >= 6 ? "text-cyan-300" : v >= 4 ? "text-amber-300" : "text-rose-300",
                          isBest && "font-semibold"
                        )}>
                          {v != null ? v.toFixed(1) : "—"}{isBest && v != null && " ★"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="border-t border-white/5">
                <td className="py-2.5 pr-3 sticky left-0 bg-[color:var(--canvas-2)] text-1 font-semibold">Notable clients</td>
                {agents.map((a) => (
                  <td key={a.id} className="py-2.5 px-3 text-2 text-xs">
                    {(a.notable_clients || []).join(", ") || "—"}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-white/5">
                <td className="py-2.5 pr-3 sticky left-0 bg-[color:var(--canvas-2)] text-1 font-semibold">Fee</td>
                {agents.map((a) => (
                  <td key={a.id} className="py-2.5 px-3 font-mono text-2">{a.fee_pct != null ? `${a.fee_pct}%` : "—"}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="ghost" className="text-2">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
